import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const http = axios.create({ baseURL: `${BACKEND_URL}/api`, withCredentials: true, timeout: 30000 });

let refreshing = null;
http.interceptors.response.use(
  (r) => r,
  async (error) => {
    const cfg = error.config || {};
    const url = cfg.url || "";
    const skip = url.includes("/auth/login") || url.includes("/auth/refresh") || cfg._retried;
    if (error.response?.status === 401 && !skip && (url.startsWith("/admin") || url.startsWith("/auth/me"))) {
      try {
        refreshing = refreshing || http.post("/auth/refresh").finally(() => { refreshing = null; });
        await refreshing;
        return http({ ...cfg, _retried: true });
      } catch {
        window.dispatchEvent(new Event("auth:expired"));
      }
    }
    return Promise.reject(error);
  }
);

export function errorMessage(err) {
  if (!err?.response) return "Could not reach the server. Please check your connection.";
  const detail = err.response.data?.detail;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => (e.field ? `${e.field}: ${e.msg}` : e.msg)).join(" · ");
  return "Something went wrong. Please try again.";
}

export const imgSrc = (url) => (url && url.startsWith("/api") ? `${BACKEND_URL}${url}` : url);

const data = (p) => p.then((r) => r.data);

export const publicApi = {
  products: (params) => data(http.get("/products", { params })),
  product: (id) => data(http.get(`/products/${id}`)),
  categories: () => data(http.get("/categories")),
  version: () => data(http.get("/catalog/version")),
  settings: () => data(http.get("/settings")),
};

export const authApi = {
  login: (body) => data(http.post("/auth/login", body)),
  logout: () => data(http.post("/auth/logout")),
  me: () => data(http.get("/auth/me")),
  changePassword: (body) => data(http.post("/auth/change-password", body)),
  forgotPassword: (email) => data(http.post("/auth/forgot-password", { email })),
  resetPassword: (body) => data(http.post("/auth/reset-password", body)),
};

export const adminApi = {
  dashboard: () => data(http.get("/admin/dashboard")),
  products: (params) => data(http.get("/admin/products", { params })),
  categories: () => data(http.get("/admin/categories")),
  addCategory: (name) => data(http.post("/admin/categories", { name })),
  create: (body) => data(http.post("/admin/products", body)),
  update: (id, body) => data(http.patch(`/admin/products/${id}`, body)),
  remove: (id) => data(http.delete(`/admin/products/${id}`)),
  uploadImage: (id, file, onProgress) => {
    const fd = new FormData();
    fd.append("file", file);
    return data(http.post(`/admin/products/${id}/images`, fd, {
      timeout: 120000,
      onUploadProgress: (e) => onProgress?.(Math.round((e.loaded / (e.total || 1)) * 100)),
    }));
  },
  deleteImage: (id, imageId) => data(http.delete(`/admin/products/${id}/images/${imageId}`)),
  reorderImages: (id, imageIds) => data(http.put(`/admin/products/${id}/images/order`, { image_ids: imageIds })),
  uploadSetting: (kind, file) => {
    const fd = new FormData();
    fd.append("file", file);
    return data(http.post(`/admin/settings/${kind}`, fd, { timeout: 120000 }));
  },
  deleteSetting: (kind) => data(http.delete(`/admin/settings/${kind}`)),
  removeDemo: () => data(http.delete("/admin/demo-data")),
};

export const ALLOWED_TYPES = ["image/jpeg", "image/png", "image/webp"];
export const MAX_BYTES = 6 * 1024 * 1024;

export function checkImageFile(file) {
  if (!ALLOWED_TYPES.includes(file.type)) return "Only JPEG, PNG or WebP images are allowed";
  if (file.size > MAX_BYTES) return "Image must be 6 MB or smaller";
  return null;
}
